import React, { useMemo, useCallback } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { orderBy, filterBy } from "@progress/kendo-data-query";
import EditableNumericCell from "../../../dashboard-util/dashboard-telerik-helper/editable-numeric-cell";
import { Tooltip } from "@fluentui/react-components";
import { ProjectIdCell, TechBankerEmptyState, renderCellContent, isCurrentUserOwner, OwnerDisabledCell } from "./tech-banker-time-log-helper";
import TechDealTeamAvatarGroupCell from "../../tech-deal-team-avatar";
import {
    getLastUpdatedColor,
    renderLastUpdatedTooltipHeader,
    formatLastUpdatedDate,
    renderTooltipHeader,
    createColumnMenu,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import  { isFilteredColumn } from "../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import PropTypes from "prop-types";

const TechBankerDetailGrid = ({ data = [], onItemChange, canEdit, canCreate, detailState, onDetailStateChange, userId }) => {
    const { sort, filter } = detailState;

    const normalizedData = useMemo(() =>
        data.map((r) => {
            if (!r.LastUpdated) return r;
            const d = r.LastUpdated instanceof Date ? r.LastUpdated : new Date(r.LastUpdated);
            if (isNaN(d.getTime())) return r;
            return { ...r, LastUpdated: new Date(d.getFullYear(), d.getMonth(), d.getDate()) };
        }),
    [data]);

    const filteredData = useMemo(() => {
        return filter ? filterBy(normalizedData, filter) : normalizedData;
    }, [normalizedData, filter]);

    const processedData = useMemo(() => {
        return sort?.length ? orderBy(filteredData, sort) : filteredData;
    }, [filteredData, sort]);

    const columnMenu = createColumnMenu(data, "");

    const columnIsFiltered = useCallback((fieldName) => {
        return isFilteredColumn(fieldName, filter);
    }, [filter]);

    const renderEditableCell = useCallback((props) => {
        if (processedData.length === 0) return null;
        return isCurrentUserOwner(props.dataItem.Members, userId)
            ? <OwnerDisabledCell value={props.dataItem[props.field]} />
            : <EditableNumericCell {...props} onChange={onItemChange} />;
    }, [processedData.length, userId, onItemChange]);

    return (
        <div className="detail-grid-scroll-container">
            <Grid
                data={processedData.length > 0 ? processedData : [{}]}
                sortable={true}
                sort={detailState.sort}
                filter={detailState.filter}
                onSortChange={(e) => onDetailStateChange({ ...detailState, sort: e.sort })}
                onFilterChange={(e) => onDetailStateChange({ ...detailState, filter: e.filter })}
                editable="true"
                editField="inEdit"
                onItemChange={onItemChange}
            >
                <Column
                    field="ProjectId"
                    width="130px"
                    editable={false}
                    title={renderTooltipHeader("#")}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("ProjectId") ? "filtered" : "" }
                    cell={(props) => 
                        renderCellContent(processedData.length, 
                        <TechBankerEmptyState canCreate={canCreate} />, 
                        <ProjectIdCell {...props} canEdit={canEdit} />
                    )}
                />
                <Column
                    field="ProjectName"
                    width="200px"
                    title={renderTooltipHeader("Deal")}
                    editable={false}
                    filter="text"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("ProjectName") ? "filtered" : "" }
                    cell={(props) =>
                        renderCellContent(processedData.length, null, (
                            <td className="background-white k-table-td">
                            <Tooltip content={props.dataItem.ProjectName} relationship="label">
                                <span>{props.dataItem.ProjectName}</span>
                            </Tooltip>
                            </td>
                        ))
                    }
                />
                <Column
                    field="LastWeekHours"
                    title={renderTooltipHeader("Last Week Hours")}
                    editor="numeric"
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("LastWeekHours") ? "filtered" : "" }
                    cell={renderEditableCell}
                />
                <Column
                    field="WeekendHours"
                    title={renderTooltipHeader("Weekend Hours")}
                    editor="numeric"
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("WeekendHours") ? "filtered" : "" }
                    cell={renderEditableCell}
                />
                <Column
                    field="TotalHours"
                    title={renderTooltipHeader("Total Hours")}
                    editable={false}
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("TotalHours") ? "filtered" : "" }
                    cell={(props) =>
                            renderCellContent(
                                processedData.length,
                                null,
                                <td className="background-white k-table-td">{props.dataItem.TotalHours}</td>
                            )
                        }
                />
                <Column
                    field="CurrentWeekHours"
                    title={renderTooltipHeader("Current Week Hours")}
                    editor="numeric"
                    filter="numeric"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("CurrentWeekHours") ? "filtered" : "" }
                    cell={renderEditableCell}
                />
                <Column
                    field="LastUpdated"
                    title={renderLastUpdatedTooltipHeader()}
                    editable={false}
                    filter="date"
                    columnMenu={columnMenu}
                    headerClassName={ columnIsFiltered("LastUpdated") ? "filtered" : "" }
                    cell={(props) => {
                        if (processedData.length === 0) return null;
                        const val = props.dataItem.LastUpdated;
                        const display = formatLastUpdatedDate(val);
                        return (
                            <td className="background-white k-table-td"
                            style={{ color: getLastUpdatedColor(val) }}
                            >
                                {display}
                            </td>
                        );
                    }}
                />
                <Column
                    field="Members"
                    title={renderTooltipHeader("Deal Team")}
                    sortable={false}
                    filterable={false}
                    editable={false}
                    cell={(props) =>
                        processedData.length === 0 ? null : (
                            <td className="background-white k-table-td">
                                <TechDealTeamAvatarGroupCell members={props.dataItem.Members} />
                            </td>
                        )
                    }
                />
            </Grid>
        </div>
    );
};

export default TechBankerDetailGrid;

TechBankerDetailGrid.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            ProjectId: PropTypes.number,
            ProjectIdString: PropTypes.string,
            ProjectName: PropTypes.string,
            LastWeekHours: PropTypes.number,
            WeekendHours: PropTypes.number,
            TotalHours: PropTypes.number,
            CurrentWeekHours: PropTypes.number,
            LastUpdated: PropTypes.string,
        })
    ).isRequired,
    onItemChange: PropTypes.func.isRequired,
    canEdit: PropTypes.bool.isRequired,
    canCreate: PropTypes.bool.isRequired,
    userId: PropTypes.number,
    detailState: PropTypes.shape({
        sort: PropTypes.arrayOf(PropTypes.object),
        filter: PropTypes.object,
    }).isRequired,
    onDetailStateChange: PropTypes.func.isRequired,
};